import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import './orisit.css';

interface HashResponse {
   [key: string]: string;
}

const Orisit: React.FC = () => {
   const { key } = useParams<{ key: string }>();
   const [data, setData] = useState<HashResponse | null>(null);
   const [error, setError] = useState<string | null>(null);
   const [loading, setLoading] = useState<boolean>(false);

   useEffect(() => {
      if (!key) return;
      setLoading(true);
      setError(null);

      const isLocalDevelopment =
         process.env.NODE_ENV === 'development' &&
         (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      const apiPath = isLocalDevelopment ? '/api/hash.local' : '/api/hash';

      fetch(`${apiPath}?key=${encodeURIComponent(key)}`)
         .then(async (res) => {
            if (!res.ok) {
               const body = await res.json().catch(() => ({}));
               throw new Error(body.error || res.statusText);
            }
            return res.json();
         })
         .then((json) => setData(json))
         .catch((err) => setError(err.message))
         .finally(() => setLoading(false));
   }, [key]);

   // parse and compute elapsed
   const parsed = data?.parsed
      ? (data.parsed as { key?: string; recipient?: string; time?: string })
      : null;

   // safely extract time string for TS
   const parsedTime = parsed?.time;
   const elapsed = parsedTime
      ? (() => {
         const then = new Date(parsedTime).getTime();
         const now = Date.now();
         const diff = now - then;
         const sec = Math.floor(diff / 1000);
         const min = Math.floor(sec / 60);
         const hr = Math.floor(min / 60);
         if (hr) return `${hr}h ${min % 60}m ago`;
         if (min) return `${min}m ${sec % 60}s ago`;
         return `${sec}s ago`;
      })()
      : null;

   return (
      <div className='orisit-page'>
         <Helmet>
            <title>Validate it is Martin</title>
         </Helmet>
         <div className='orisit-panel'>
            <h1 className='orisit-title'>Martin - Orisit Lookup</h1>
            <p className='orisit-cta'>Compare these values with the details you were sent.</p>
            {loading && <p className='orisit-loading'>Loading...</p>}
            {error && <p className='orisit-error'>Do not trust this signature.Error: {error}</p>}

            {parsed && (
               <div className='parsed-summary'>
                  <div className='orisit-row'>
                     <div className='orisit-value-box'>{parsed.key || '-'}</div>
                     <div className='orisit-question-box'>Is this the word they used?</div>
                  </div>
                  <div className='orisit-row'>
                     <div className='orisit-value-box'>{parsed.recipient || '-'}</div>
                     <div className='orisit-question-box'>Is this you?</div>
                  </div>
                  <div className='orisit-row'>
                     <div className='orisit-value-box'>
                        {parsedTime ? new Date(parsedTime).toLocaleString() : '-'}
                     </div>
                     <div className='orisit-question-box'>
                        Was this around when they sent it?
                        {elapsed && <span className='orisit-elapsed'>({elapsed})</span>}
                     </div>
                  </div>
               </div>
            )}
            <p className='orisit-warning'>If anything looks amiss do not trust this signature</p>

            {data && (
               <>
                  <section className='orisit-trust'>
                     <h2 className='orisit-trust-title'>Why trust this?</h2>
                     <p>
                        These signatures are created using a private key not associated with this
                        website and placed in a database independently. This website then retrieves
                        them and decrypts them using my public certificate.
                     </p>
                     <p><b>If you trust that I am in control of this website</b> - You are good to go.</p>
                     <p>
                        <b>If you do not trust that</b> - You can access the public certificate via this URL
                        and have either yourself or your local nerd validate it works for the
                        cipher in "Data used".
                     </p>
                     <p>
                        <a
                           href='https://github.com/MJRule/public/blob/main/publiccertificate.pem'
                           target='_blank'
                           rel='noreferrer'
                        >
                           https://github.com/MJRule/public/blob/main/publiccertificate.pem
                        </a>
                     </p>
                     <p>
                        <b>If you don't trust any of this</b> - Then the person has access to the platform you were
                        messaged on, my website, my GitHub, and my computer, so please come rescue
                        me, I have been kidnapped.
                     </p>
                  </section>
                  <h2 className='orisit-data-title'>Data used</h2>
                  <pre className='orisit-raw-data'>
                     {JSON.stringify(data, null, 2)}
                  </pre>
               </>
            )}
         </div>
      </div>
   );
};

export default Orisit;
